import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import {
  EisFundStartup,
  InvestmentStatus,
  TaxBenefit,
} from "@components/EisFund/types";
import {
  CommitmentInvestmentsCache,
  PaginationState,
  RequestStatus,
  INITIAL_COMMITMENT_CACHE,
  INITIAL_PAGINATION,
} from "./eisFund.types";
import { generateFilterKey } from "./eisFund.actions";

// ============================================================================
// Base Selectors
// ============================================================================

/**
 * Get the entire EIS Fund state
 */
export const selectEisFundState = (state: RootState) => state.eisFund;

/**
 * Get all deals in the cache
 */
export const selectEisFundDeals = createSelector(
  [selectEisFundState],
  (eisFund) => eisFund?.deals || {}
);

// ============================================================================
// Memoized Parameterized Selectors
// ============================================================================

/**
 * Get a specific deal's cache
 * Path: eisFund.deals[dealId]
 */
export const selectDealCache = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
  ],
  (eisFund, dealId) => eisFund?.deals?.[dealId] || { commitments: {} }
);

/**
 * Get a specific commitment's cache
 * Path: eisFund.deals[dealId].commitments[commitmentId]
 */
export const selectCommitmentCache = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): CommitmentInvestmentsCache =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId] ||
    INITIAL_COMMITMENT_CACHE
);

// ============================================================================
// Investment Selectors
// ============================================================================

/**
 * Get investments for a specific deal/commitment
 */
export const selectInvestments = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): EisFundStartup[] =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.investments || []
);

/**
 * Get investment count for a specific deal/commitment
 */
export const selectInvestmentCount = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): number =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.investments
      ?.length || 0
);

// ============================================================================
// Status Selectors
// ============================================================================

/**
 * Get request status
 */
export const selectStatus = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): RequestStatus =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.status || "idle"
);

/**
 * Check if currently loading (initial fetch)
 */
export const selectIsLoading = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): boolean =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.status === "loading"
);

/**
 * Check if loading more (pagination)
 */
export const selectIsLoadingMore = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): boolean =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.status ===
    "loadingMore"
);

/**
 * Get error message
 */
export const selectError = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): string | null =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.error ?? null
);

// ============================================================================
// Pagination Selectors
// ============================================================================

/**
 * Get pagination state
 */
export const selectPagination = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): PaginationState =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.pagination ||
    INITIAL_PAGINATION
);

/**
 * Check if there are more items to load
 */
export const selectHasNextPage = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): boolean =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.pagination
      ?.hasNextPage ?? true
);

/**
 * Get total count of investments
 */
export const selectTotalCount = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): number =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.pagination
      ?.totalCount ?? 0
);

/**
 * Get current page number
 */
export const selectCurrentPage = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): number =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.pagination
      ?.currentPage ?? 1
);

// ============================================================================
// Cache Validation Selectors
// ============================================================================

/**
 * Get last fetched timestamp
 */
export const selectLastFetched = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): number | null =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.lastFetched ?? null
);

/**
 * Get filter key (for cache validation)
 */
export const selectFilterKey = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): string =>
    eisFund?.deals?.[dealId]?.commitments?.[commitmentId]?.filterKey ?? ""
);

/**
 * Check if cache is valid for given filters.
 * Returns true if cache exists and matches current filters.
 */
export const selectIsCacheValid = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
    (
      _state: RootState,
      _dealId: string,
      _commitmentId: string,
      dealName?: string
    ) => dealName,
    (
      _state: RootState,
      _dealId: string,
      _commitmentId: string,
      _dealName?: string,
      taxBenefit?: TaxBenefit | "all"
    ) => taxBenefit,
    (
      _state: RootState,
      _dealId: string,
      _commitmentId: string,
      _dealName?: string,
      _taxBenefit?: TaxBenefit | "all",
      statusLabelNames?: InvestmentStatus[]
    ) => statusLabelNames,
  ],
  (
    eisFund,
    dealId,
    commitmentId,
    dealName,
    taxBenefit,
    statusLabelNames
  ): boolean => {
    const cache = eisFund?.deals?.[dealId]?.commitments?.[commitmentId];
    if (!cache || cache.lastFetched === null) {
      return false;
    }

    const currentFilterKey = generateFilterKey(
      dealName,
      taxBenefit,
      statusLabelNames
    );
    return cache.filterKey === currentFilterKey;
  }
);

/**
 * Check if data has been loaded at least once
 */
export const selectHasData = createSelector(
  [
    selectEisFundState,
    (_state: RootState, dealId: string) => dealId,
    (_state: RootState, _dealId: string, commitmentId: string) => commitmentId,
  ],
  (eisFund, dealId, commitmentId): boolean => {
    const cache = eisFund?.deals?.[dealId]?.commitments?.[commitmentId];
    return cache !== undefined && cache.lastFetched !== null;
  }
);
